import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import { useAuth } from "./AuthContext";
import Configs from "../utils/Configs";
import AppConstants from "../constants/appConstants";
import StorageKeys from "../constants/storageKeys";

const WebSocketContext = createContext();
export const useWebSocket = () => useContext(WebSocketContext);

const WebSocketProvider = ({ children }) => {
  const { user } = useAuth();

  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [queuedMessages, setQueuedMessages] = useState([]);

  const socketRef = useRef(null);
  const heartbeatIntervalRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const lastPongTimeRef = useRef(Date.now());
  const handlersRef = useRef({});

  const isMounted = useRef(true);

  const handleOpen = () => {
    if (!isMounted.current) return;
    console.log("✅ WebSocket connected");
    setIsConnected(true);
    reconnectAttempts.current = 0;
    lastPongTimeRef.current = Date.now();
    startHeartbeat();
  };

  const registerHandler = useCallback((tag, handler) => {
    if (!tag) return;
    if (handler) {
      handlersRef.current[tag] = handler;
      console.log(`[Handler] Registered handler for tag: ${tag}`);
    } else {
      delete handlersRef.current[tag];
      console.log(`[Handler] Unregistered handler for tag: ${tag}`);
    }
  }, []);

  const handleClose = () => {
    if (!isMounted.current) return;
    console.log("⚠️ WebSocket disconnected");
    setIsConnected(false);
    stopHeartbeat();
    scheduleReconnect();
  };

  const handleError = (error) => {
    if (!isMounted.current) return;
    console.error("❌ WebSocket error:", error);
  };

  const handleMessage = (event) => {
    if (!isMounted.current) return;

    console.log("🔄 Raw WebSocket message:", event.data);
    try {
      const data = JSON.parse(event.data);
      console.log("Received WebSocket message:", data);

      if (data.tag === "pong") {
        lastPongTimeRef.current = Date.now();
      }

      const handler = handlersRef.current[data.tag];
      if (handler) {
        handler(data);
      } else {
        console.warn(
          `[Handler] No handler found for tag: ${data.tag}. Queuing message.`
        );
        setQueuedMessages((prev) => [...prev, data]);
      }
    } catch (err) {
      console.error("❌ Error parsing WebSocket message:", err);
    }
  };

  const dequeueMessages = useCallback((tag) => {
    let relevantMessages = [];
    setQueuedMessages((prev) => {
      relevantMessages = prev.filter((msg) => msg.tag === tag);
      return prev.filter((msg) => msg.tag !== tag);
    });
    return relevantMessages;
  }, []); // Note: setQueuedMessages has a stable reference

  const initializeSocket = () => {
    if (!user?.user_id) {
      console.error("❌ Cannot initialize WebSocket: Missing user ID");
      return;
    }

    const accessToken = localStorage.getItem(StorageKeys.ACCESS_TOKEN);
    if (!accessToken) {
      console.error("❌ Cannot initialize WebSocket: Missing access token");
      return;
    }

    const socketUrl = `${Configs.wsUserUrl}${user.user_id}/?token=${encodeURIComponent(accessToken)}`;

    if (socketRef.current) {
      socketRef.current.onopen = null;
      socketRef.current.onclose = null;
      socketRef.current.onerror = null;
      socketRef.current.onmessage = null;
      socketRef.current.close();
    }

    const newSocket = new WebSocket(socketUrl);
    newSocket.onopen = handleOpen;
    newSocket.onclose = handleClose;
    newSocket.onerror = handleError;
    newSocket.onmessage = handleMessage;

    socketRef.current = newSocket;
    setSocket(newSocket);
  };

  const startHeartbeat = () => {
    stopHeartbeat();
    heartbeatIntervalRef.current = setInterval(() => {
      const now = Date.now();
      const isSocketOpen = socketRef.current?.readyState === WebSocket.OPEN;

      if (isSocketOpen && user?.user_id) {
        sendMessage({
          user_id: user.user_id,
          tag: "ping",
          payload: { user_id: user.user_id },
        });

        const timeSinceLastPong = now - lastPongTimeRef.current;
        if (timeSinceLastPong > AppConstants.WS_PONG_TIMEOUT_MS) {
          console.warn("⏱️ No pong received in 30s. Closing WebSocket.");
          socketRef.current.close();
        }
      }
    }, AppConstants.WS_HEARTBEAT_INTERVAL_MS);
  };

  const stopHeartbeat = () => {
    if (heartbeatIntervalRef.current) {
      clearInterval(heartbeatIntervalRef.current);
      heartbeatIntervalRef.current = null;
    }
  };

  const scheduleReconnect = () => {
    if (!user?.user_id || reconnectTimeoutRef.current) return;

    reconnectAttempts.current += 1;
    const delay = Math.min(
      AppConstants.WS_MAX_RECONNECT_BACKOFF_MS,
      1000 * 2 ** reconnectAttempts.current,
    );
    console.log(`🔁 Attempting reconnect in ${delay / 1000}s`);

    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectTimeoutRef.current = null;
      initializeSocket();
    }, delay);
  };

  const cancelReconnect = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
  };

  const sendMessage = useCallback((message) => {
    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
      console.log("📤 Message sent:", message);
    } else {
      console.error("❌ Cannot send message, WebSocket not connected");
    }
  }, []); // The reference of socketRef.current is stable

  const sendAction = useCallback(
    (payload) => {
      if (socketRef.current?.readyState === WebSocket.OPEN) {
        const message = {
          user_id: user?.user_id,
          tag: "action",
          payload,
        };
        socketRef.current.send(JSON.stringify(message));
        console.log("⚙️ Action sent:", message);
      } else {
        console.error("❌ Cannot send action, WebSocket not connected");
      }
    },
    [user]
  );

  useEffect(() => {
    isMounted.current = true;

    if (user?.user_id) {
      initializeSocket();
    }

    return () => {
      isMounted.current = false;

      stopHeartbeat();
      cancelReconnect();

      if (socketRef.current) {
        socketRef.current.close();
        console.log("🧹 WebSocket closed during cleanup");
      }
    };
  }, [user]);

  return (
    <WebSocketContext.Provider
      value={{
        socket,
        isConnected,
        sendMessage,
        sendAction,
        registerHandler,
        dequeueMessages,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export default WebSocketProvider;
